import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Form, Switch, InputNumber, Select, Upload, Button, message, Spin,
  Image, Space, Alert, Descriptions, Tag, Table, Collapse, Input, Row, Col,
  Divider, Tooltip, Typography
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  UserOutlined,
  ControlOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  RocketOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

const LOCALE_OPTIONS = [
  { value: '', label: '' },
  { value: 'zh', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
];

const { Text } = Typography;

import ListPageHeader from '@shared/ListPageHeader';
import { settingsService } from '@services/admin/settingsService';
import { useTranslation } from 'react-i18next';

export default function GeneralSettings() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogoFlag, setRemoveLogoFlag] = useState(false);
  const [inventoryTraceabilityMode, setInventoryTraceabilityMode] = useState('aggregated');
  // Readonly：3PL ↔ merchant 合約層級的決策，admin 端只能查看，不能編輯（要改請聯絡客服）。
  const [serialTrackingPolicy, setSerialTrackingPolicy] = useState('outbound_only');
  const [merchantData, setMerchantData] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await settingsService.get();
      if (data.success) {
        const settings = data.data;
        setMerchantData(settings);
        form.setFieldsValue({
          name: settings.name,
          vat_number: settings.vat_number,
          phone: settings.phone,
          address: settings.address,
          city: settings.city,
          postal_code: settings.postal_code,
          locale: settings.locale || '',
          enable_de_identification: settings.enable_de_identification,
          de_identify_days: settings.de_identify_days,
          de_identify_mechanism: settings.de_identify_mechanism,
          expiration_fallback_enabled: settings.expiration_fallback_enabled,
          fulfillment_lot_policy: settings.fulfillment_lot_policy,
          default_allow_partial_allocation: settings.default_allow_partial_allocation,
        });
        setLogoUrl(settings.logo_url);
        setInventoryTraceabilityMode(settings.inventory_traceability_mode || 'aggregated');
        setSerialTrackingPolicy(settings.serial_tracking_policy || 'outbound_only');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [form, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const formData = new FormData();

      // 添加表單欄位
      formData.append('name', values.name || '');
      formData.append('vat_number', values.vat_number || '');
      formData.append('phone', values.phone || '');
      formData.append('address', values.address || '');
      formData.append('city', values.city || '');
      formData.append('postal_code', values.postal_code || '');
      formData.append('enable_de_identification', values.enable_de_identification || false);
      if (values.de_identify_days !== undefined && values.de_identify_days !== null) {
        formData.append('de_identify_days', values.de_identify_days);
      }
      if (values.de_identify_mechanism) {
        formData.append('de_identify_mechanism', values.de_identify_mechanism);
      }
      formData.append('locale', values.locale || '');
      formData.append('expiration_fallback_enabled', values.expiration_fallback_enabled || false);
      formData.append('fulfillment_lot_policy', values.fulfillment_lot_policy || 'flexible');
      formData.append('default_allow_partial_allocation', values.default_allow_partial_allocation ?? true);

      // 處理 logo
      if (logoFile) {
        formData.append('logo_file', logoFile);
      }
      if (removeLogoFlag) {
        formData.append('remove_logo', 'true');
      }

      const data = await settingsService.update(formData);
      if (data.success) {
        message.success(t('common.submit.success'));
        setLogoUrl(data.data.logo_url);
        setLogoFile(null);
        setRemoveLogoFlag(false);
      } else {
        message.error(data.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (info) => {
    const file = info.file;
    if (file) {
      setLogoFile(file);
      setRemoveLogoFlag(false);
      // 預覽
      const reader = new FileReader();
      reader.onload = (e) => setLogoUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    setLogoFile(null);
    setRemoveLogoFlag(true);
  };

  const comparisonData = [
    {
      key: 'aggregated',
      mode: t('admin.settings.general.traceability_modes.aggregated'),
      behavior: t('admin.settings.general.traceability_comparison.aggregated_behavior'),
      scenario: t('admin.settings.general.traceability_comparison.aggregated_scenario'),
      isCurrent: inventoryTraceabilityMode === 'aggregated',
    },
    {
      key: 'isolated',
      mode: t('admin.settings.general.traceability_modes.isolated'),
      behavior: t('admin.settings.general.traceability_comparison.isolated_behavior'),
      scenario: t('admin.settings.general.traceability_comparison.isolated_scenario'),
      isCurrent: inventoryTraceabilityMode === 'isolated',
    },
  ];

  const comparisonColumns = [
    {
      title: t('admin.settings.general.traceability_comparison.col_mode'),
      dataIndex: 'mode',
      key: 'mode',
      width: '25%',
      render: (text, record) => (
        <Space>
          <span style={{ fontWeight: record.isCurrent ? 'bold' : 'normal' }}>{text}</span>
          {record.isCurrent && <Tag color="blue">{t('common.status.current') || 'Current'}</Tag>}
        </Space>
      ),
    },
    {
      title: t('admin.settings.general.traceability_comparison.col_behavior'),
      dataIndex: 'behavior',
      key: 'behavior',
      width: '45%',
    },
    {
      title: t('admin.settings.general.traceability_comparison.col_scenario'),
      dataIndex: 'scenario',
      key: 'scenario',
      width: '30%',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      <ListPageHeader
        basePath="/admin"
        items={['settings', 'settings.general']}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          enable_de_identification: false,
          expiration_fallback_enabled: false,
          fulfillment_lot_policy: 'flexible',
          default_allow_partial_allocation: true,
        }}
      >
        <Row gutter={[24, 24]}>
          {/* Left Column: Basic Information */}
          <Col xs={24} lg={12}>
            <Card
              title={<span><UserOutlined /> {t('admin.settings.general.sections.profile')}</span>}
              height="100%"
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="name"
                    label={t('admin.settings.general.name')}
                    rules={[{ required: true, message: t('common.validation.required') }]}
                  >
                    <Input placeholder={t('admin.settings.general.name')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="locale"
                    label={
                      <Space>
                        <GlobalOutlined />
                        {t('admin.settings.general.locale')}
                      </Space>
                    }
                    extra={t('admin.settings.general.locale_help')}
                  >
                    <Select
                      allowClear
                      placeholder={t('admin.settings.general.locale_inherit')}
                      options={LOCALE_OPTIONS.filter(o => o.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="vat_number"
                    label={t('admin.settings.general.vat_number')}
                  >
                    <Input placeholder={t('admin.settings.general.vat_number')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label={t('admin.settings.general.phone')}
                  >
                    <Input placeholder={t('admin.settings.general.phone')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="postal_code"
                    label={t('admin.settings.general.postal_code')}
                  >
                    <Input placeholder={t('admin.settings.general.postal_code')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="city"
                    label={t('admin.settings.general.city')}
                  >
                    <Input placeholder={t('admin.settings.general.city')} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="address"
                    label={t('admin.settings.general.address')}
                  >
                    <Input placeholder={t('admin.settings.general.address')} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Form.Item label={t('admin.settings.general.logo')}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{
                    border: '1px dashed #d9d9d9',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    background: '#fafafa'
                  }}>
                    {logoUrl ? (
                      <div style={{ marginBottom: 16 }}>
                        <Image
                          src={logoUrl}
                          alt="Merchant Logo"
                          style={{ maxWidth: 200, maxHeight: 100, objectFit: 'contain' }}
                          preview={false}
                        />
                      </div>
                    ) : (
                      <div style={{ padding: '20px 0', color: '#999' }}>
                        {t('common.status.none') || 'No Logo'}
                      </div>
                    )}
                    <Space>
                      <Upload
                        beforeUpload={() => false}
                        onChange={handleLogoChange}
                        showUploadList={false}
                        accept="image/*"
                      >
                        <Button icon={<UploadOutlined />} type={logoUrl ? 'default' : 'primary'}>
                          {t('admin.settings.general.upload_logo')}
                        </Button>
                      </Upload>
                      {logoUrl && (
                        <Button
                          icon={<DeleteOutlined />}
                          danger
                          onClick={handleRemoveLogo}
                        >
                          {t('admin.settings.general.remove_logo')}
                        </Button>
                      )}
                    </Space>
                  </div>
                </Space>
              </Form.Item>
            </Card>
          </Col>

          {/* Right Column: Fulfillment and Privacy */}
          <Col xs={24} lg={12}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              {/* Fulfillment Logic Card */}
              <Card title={<span><ControlOutlined /> {t('admin.settings.general.sections.fulfillment')}</span>}>
                <Form.Item
                  name="expiration_fallback_enabled"
                  label={
                    <Space>
                      {t('admin.settings.general.expiration_fallback_enabled')}
                      <Tooltip title={t('admin.settings.general.expiration_fallback_tooltip')}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                  extra={<small>{t('admin.settings.general.expiration_fallback_description')}</small>}
                >
                  <Switch />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }} />

                <Form.Item
                  name="fulfillment_lot_policy"
                  label={
                    <Space>
                      {t('admin.settings.general.fulfillment_lot_policy')}
                      <Tooltip title={t('admin.settings.general.fulfillment_lot_policy_tooltip')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: t('common.validation.required') }]}
                  extra={<small>{t('admin.settings.general.fulfillment_lot_policy_description')}</small>}
                >
                  <Select>
                    <Select.Option value="flexible">{t('admin.settings.general.fulfillment_lot_policies.flexible')}</Select.Option>
                    <Select.Option value="strict">{t('admin.settings.general.fulfillment_lot_policies.strict')}</Select.Option>
                  </Select>
                </Form.Item>

                <Divider style={{ margin: '12px 0' }} />

                <Form.Item
                  name="default_allow_partial_allocation"
                  label={
                    <Space>
                      {t('admin.settings.general.default_allow_partial_allocation')}
                      <Tooltip title={t('admin.settings.general.default_allow_partial_allocation_tooltip')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                  extra={<small>{t('admin.settings.general.default_allow_partial_allocation_description')}</small>}
                >
                  <Switch />
                </Form.Item>
              </Card>

              {/* Privacy Settings Card */}
              <Card title={<span><SafetyCertificateOutlined /> {t('admin.settings.general.sections.privacy')}</span>}>
                <Form.Item
                  name="enable_de_identification"
                  label={
                    <Space>
                      {t('admin.settings.general.enable_de_identification')}
                      <Tooltip title={t('admin.settings.general.enable_de_identification_tooltip')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, current) => prev.enable_de_identification !== current.enable_de_identification}
                >
                  {({ getFieldValue }) =>
                    getFieldValue('enable_de_identification') ? (
                      <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="de_identify_days"
                              label={
                                <Space>
                                  {t('admin.settings.general.de_identify_days')}
                                  <Tooltip title={t('admin.settings.general.de_identify_days_tooltip')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                  </Tooltip>
                                </Space>
                              }
                            >
                              <InputNumber min={1} max={365} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="de_identify_mechanism"
                              label={
                                <Space>
                                  {t('admin.settings.general.de_identify_mechanism')}
                                  <Tooltip title={t('admin.settings.general.de_identify_mechanism_tooltip')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                  </Tooltip>
                                </Space>
                              }
                            >
                              <Select style={{ width: '100%' }}>
                                <Select.Option value="masked">
                                  {t('admin.settings.general.mechanism_masked')}
                                </Select.Option>
                                <Select.Option value="deleted">
                                  {t('admin.settings.general.mechanism_deleted')}
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    ) : null
                  }
                </Form.Item>
              </Card>

              {/* SLA Settings Card (Read-only for Admin) */}
              <Card
                title={<span><RocketOutlined /> {t('admin.settings.general.sla_settings.title')}</span>}
                extra={
                  merchantData?.sla_enabled ? (
                    <Tag color="success">{t('admin.settings.general.sla_settings.enabled')}</Tag>
                  ) : (
                    <Tag>{t('admin.settings.general.sla_settings.disabled')}</Tag>
                  )
                }
              >
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">
                    {t('admin.settings.general.sla_settings.description')}
                  </Text>
                </div>

                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={t('admin.settings.general.sla_settings.cutoff_time')}>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {merchantData?.sla_cutoff_time || '-'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('admin.settings.general.sla_settings.grace_period')}>
                    {merchantData?.sla_grace_period_minutes || 0} {t('admin.settings.general.sla_settings.grace_period_unit')}
                  </Descriptions.Item>
                </Descriptions>

                <Alert
                  message={t('admin.settings.general.sla_settings.contact_3pl')}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </Card>
            </Space>
          </Col>

          {/* Bottom Row: System Config & Save Button */}
          <Col span={24}>
            <Card title={<span><DatabaseOutlined /> {t('admin.settings.general.sections.system')}</span>}>
              <div style={{ marginBottom: 24 }}>
                <Descriptions column={{ xxl: 2, xl: 2, lg: 1, md: 1, sm: 1, xs: 1 }} bordered size="small">
                  <Descriptions.Item label={t('admin.settings.general.inventory_traceability_mode')}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Tag color={inventoryTraceabilityMode === 'isolated' ? 'purple' : 'green'} style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {t(`admin.settings.general.traceability_modes.${inventoryTraceabilityMode}`)}
                        </Tag>
                        <span style={{ color: '#666' }}>
                          {t(`admin.settings.general.traceability_details.${inventoryTraceabilityMode}`)}
                        </span>
                      </Space>
                      <Alert
                        message={t('admin.settings.general.inventory_traceability_contact')}
                        type="info"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('admin.settings.general.serial_tracking_policy')}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Tag color={serialTrackingPolicy === 'full_lifecycle' ? 'purple' : 'green'} style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {t(`admin.settings.general.serial_tracking_policies.${serialTrackingPolicy}`)}
                        </Tag>
                        <span style={{ color: '#666' }}>
                          {t(`admin.settings.general.serial_tracking_details.${serialTrackingPolicy}`)}
                        </span>
                      </Space>
                      <Alert
                        message={t('admin.settings.general.serial_tracking_contact')}
                        type="info"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </Space>
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <Collapse
                ghost
                items={[{
                  key: '1',
                  label: <span style={{ color: '#1890ff', fontWeight: 500 }}>{t('admin.settings.general.traceability_comparison.title')}</span>,
                  children: (
                    <Table
                      dataSource={comparisonData}
                      columns={comparisonColumns}
                      pagination={false}
                      size="small"
                      bordered
                      rowClassName={(record) => record.isCurrent ? 'ant-table-row-selected' : ''}
                      style={{ background: '#fff' }}
                    />
                  )
                }]}
              />
            </Card>
          </Col>

          {/* Floating or Bottom Save Bar */}
          <Col span={24}>
            <div style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              justifyContent: 'flex-end',
              border: '1px solid #f0f0f0'
            }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
                size="large"
                style={{ minWidth: 120 }}
              >
                {t('common.button.save')}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
}